import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { PropertyService } from '../../services/property.service';
import { quoteComparison, quoteComparisonResponce } from '../../models/RequestQuote';
import { CommonModule } from '@angular/common';
import { IndicationQuoteStepService } from '../../services/indicationQuoteStep.service';
import { AuthService } from '../../auth/auth.service';
import { GetUserDetails } from '../../models/sessionStorage.model';
import { MatIconModule } from '@angular/material/icon';
import { EqbdConfirmationDialogComponent } from '../utils/eqbd-confirmation-dialog/eqbd-confirmation-dialog.component';
import { EQBD_HIGH_BPP_RESTRICTED_DESCRIPTIONS } from '../../enums/config.enum';

@Component({
  selector: 'app-quote-comparison',
  standalone: true,
  imports: [MatIconModule,MatDialogModule, MatButtonModule, MatSortModule, MatPaginatorModule, MatInputModule, MatFormFieldModule, MatTableModule, CommonModule],
  templateUrl: './quote-comparison.component.html',
  styleUrl: './quote-comparison.component.scss'
})
export class QuoteComparisonComponent implements OnInit {
  quotes = signal<quoteComparison[]>([]);
  branchId = signal<number>(0);
  indicationId = signal<number>(0);
  quoteId = signal<number>(0); 
  propertyService = inject(PropertyService);
  dialog = inject(MatDialog);
  indicationService = inject(IndicationQuoteStepService)
  authService = inject(AuthService)
  selectedQuote = signal<quoteComparison | null>(null);
  storageData = signal<GetUserDetails | null>(null);
  ragIndicationColor: WritableSignal<string> = signal("#000");
  
  constructor(private dialogRef: MatDialogRef<QuoteComparisonComponent>) {}

  ngOnInit(): void {
    
    const storageData = this.authService.getUserDetailsFromStorage();
    this.storageData.set(storageData);
    this.indicationId.set(Number (storageData.indicationId))
    this.branchId.set(Number (storageData.branchId))
    this.quoteId.set(Number(storageData.quoteId))
    this.fetchQuotes()
  }  

  fetchQuotes() {
    this.indicationService.fetchQuoteCoverageSummaryDetails(this.quoteId() , this.branchId()).subscribe({
      next: (response: any) => {
        if (response) {
           // Sort the response based on versionNo
           const sortedQuotes = response.result.sort((a: any, b: any) => a.versionNo - b.versionNo);
           this.quotes.set(sortedQuotes);
           this.setDefaultQuoteSelection(sortedQuotes);
        }
      },
      error: (err) => {
        console.error('Error fetching quotes:', err);
      },
    });
  }

  private parseSlTaxFeeDetails(slTaxFeeDetails: any[]): {
    policyFee: number;
    surplusLinesTax: number;
    additional: { description: string; otherDescription?: string; displayDescription: string; feeAmount: number }[];
  } {
    const details = Array.isArray(slTaxFeeDetails) ? slTaxFeeDetails : [];
    let policyFee = 0;
    let surplusLinesTax = 0;
    const additional: { description: string; otherDescription?: string; displayDescription: string; feeAmount: number }[] = [];

    details.forEach((item: any) => {
      const description = String(item?.slFeeDescription || '').trim();
      const feeAmount = Number(item?.feeAmount ?? 0);
      const amount = isNaN(feeAmount) ? 0 : feeAmount;
      const normalizedDesc = description.toLowerCase();

      if (normalizedDesc === 'policy fee') {
        policyFee = amount;
      } else if (normalizedDesc === 'surplus lines tax') {
        surplusLinesTax = amount;
      } else if (description) {
        const otherDescription = String(item?.otherDescription || '').trim();
        additional.push({
          description,
          otherDescription,
          displayDescription: otherDescription || description,
          feeAmount: amount
        });
      }
    });

    return { policyFee, surplusLinesTax, additional };
  }

  getPolicyFeeForVersion(version: quoteComparison): number {
    return this.parseSlTaxFeeDetails(version?.slTaxFeeDetails || []).policyFee;
  }

  getSurplusLinesTaxForVersion(version: quoteComparison): number {
    return this.parseSlTaxFeeDetails(version?.slTaxFeeDetails || []).surplusLinesTax;
  }

  getAdditionalFeeDescriptions(): string[] {
    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const q of this.quotes()) {
      for (const item of this.parseSlTaxFeeDetails(q?.slTaxFeeDetails || []).additional) {
        if (!seen.has(item.displayDescription)) {
          seen.add(item.displayDescription);
          ordered.push(item.displayDescription);
        }
      }
    }
    return ordered;
  }

  getAdditionalFeeForVersion(version: quoteComparison, description: string): number {
    const item = this.parseSlTaxFeeDetails(version?.slTaxFeeDetails || []).additional.find(
      (a) => a.displayDescription === description
    );
    return item ? item.feeAmount : 0;
  }

  getTotalPolicyPremiumForVersion(version: quoteComparison): number {
    const parsed = this.parseSlTaxFeeDetails(version?.slTaxFeeDetails || []);
    const additionalSum = parsed.additional.reduce((sum, a) => {
      const n = Number(a.feeAmount);
      return sum + (isNaN(n) ? 0 : n);
    }, 0);
    const commercialProperty = Number(version?.targetPremium ?? 0);
    const eqbd = Number(version?.eqbdPremium ?? 0);
    const inspection = Number(version?.inspectionFee ?? 0);
    return (
      (isNaN(commercialProperty) ? 0 : commercialProperty)
      + (isNaN(eqbd) ? 0 : eqbd)
      + (isNaN(inspection) ? 0 : inspection)
      + parsed.policyFee
      + parsed.surplusLinesTax
      + additionalSum
    );
  }

  setDefaultQuoteSelection(sortedQuotes: quoteComparison[]) {
    if (sortedQuotes.length === 0) {
      return;
    }

    // Check if there's a stored selection in sessionStorage
    const storedSelection = this.indicationService.getSelectedQuoteVersion();
    
    if (storedSelection && storedSelection.quoteId === this.quoteId()) {
      // Find the quote matching the stored version
      const matchingQuote = sortedQuotes.find(
        (quote) => quote.versionNo === storedSelection.versionNo && quote.quoteId === storedSelection.quoteId
      );
      
      if (matchingQuote) {
        // Select the stored version
        this.selectedQuote.set(matchingQuote);
        return;
      }
    }

    // If no stored selection or not found, select the quote with the highest version number
    // (last item in the sorted array since it's sorted ascending)
    const highestVersionQuote = sortedQuotes[sortedQuotes.length - 1];
    this.selectedQuote.set(highestVersionQuote);
    
    // Save the default selection to sessionStorage
    this.indicationService.setSelectedQuoteVersion(highestVersionQuote);
  }

  getEnhancementData(value: number){
    return value === 1 ? "Property Enhancement Silver" : "Property Enhancement Gold";
}

  getMaxAddressIndex(): number[] {
    const maxIndex = Math.max(...this.quotes().map((q) => q.addresses.length));
    return Array.from({ length: maxIndex }, (_, i) => i);
  }
  
  getOLBuildings(buildings: any[]) {
    // Filter buildings based on oandLFlag being 1
    const oLBuildings = buildings.filter((building) => building.oandLFlag === 1);
    // If we have any O&L buildings, join them with commas
    
    return oLBuildings.map((building, index) => `Building ${index + 1}`).join(", ");  
    
  }
  
  

  onQuoteSelect(quote: quoteComparison) {
    this.selectedQuote.set(quote);
    // Save the selection to sessionStorage immediately when user selects
    this.indicationService.setSelectedQuoteVersion(quote);
  }

  private getEQBDReferralContext(): { id: number; type: string } | null {
    const storageData = this.authService.getUserDetailsFromStorage();
    if (!storageData || storageData?.isPolicyType) {
      return null;
    }
    if (storageData?.quoteType === 'indication') {
      const indicationId = Number(storageData?.indicationId ?? 0);
      return indicationId ? { id: indicationId, type: 'indication' } : null;
    }
    const quoteId = Number(storageData?.quoteId ?? 0);
    return quoteId ? { id: quoteId, type: 'quote' } : null;
  }

  private closeWithSelectedQuote(test: quoteComparison): void {
    this.indicationService.setSelectedQuoteVersion(this.selectedQuote());
    this.indicationService.addQuoteComparison(test);
    this.dialogRef.close({ action: 'selected', quote: test });
  }

  private openEqbdConfirmationDialog(test: quoteComparison): void {
    const dialog = this.dialog.open(EqbdConfirmationDialogComponent, {
      data: {
        message: "EQBD is not included in the current version of this quote. To include EQBD, please click Amend Quote. Otherwise, click Request Bind to proceed without EQBD",
        confirmText: "Amend Quote",
        cancelText: "Request Bind"
      }
    });

    dialog.afterClosed().subscribe((result: boolean) => {
      if (result === true) {
        this.dialogRef.close({ action: 'amend' });
      } else if (result === false) {
        this.indicationService.setSelectedQuoteVersion(this.selectedQuote());
        this.indicationService.addQuoteComparison(test);
        this.dialogRef.close({ action: 'request-bind', quote: test });
        
      }
    });
  }

  private hasRestrictedOccupancyHighBpp(versionData: any): boolean {
    const restrictedDescriptions = new Set(
      EQBD_HIGH_BPP_RESTRICTED_DESCRIPTIONS.map((desc) => desc.toLowerCase())
    );
    const addresses = Array.isArray(versionData?.addresses) ? versionData.addresses : [];
    const topLevelBuildings = Array.isArray(versionData?.buildings) ? versionData.buildings : [];

    return addresses.some((address: any) => {
      const occupancy = String(address?.occupany ?? address?.occupancy ?? '').trim().toLowerCase();
      if (!restrictedDescriptions.has(occupancy)) {
        return false;
      }

      const directBpp = Number(address?.bppLimit ?? address?.businessPropertyLimit ?? 0);
      const addressBuildings = topLevelBuildings.filter(
        (building: any) => Number(building?.quoteAddressId ?? building?.addressId ?? building?.policyAddressId) === Number(address?.addressId)
      );
      const buildingsToCheck = addressBuildings.length > 0
        ? addressBuildings
        : (Array.isArray(address?.buildings) ? address.buildings : []);
      const maxBuildingBpp = buildingsToCheck.reduce((max: number, building: any) => {
        const bpp = Number(building?.bppLimit ?? building?.businessPropertyLimit ?? 0);
        return isNaN(bpp) ? max : Math.max(max, bpp);
      }, 0);
      const resolvedBpp = !isNaN(directBpp) && directBpp > 0 ? directBpp : maxBuildingBpp;
      return resolvedBpp > 49999;
    });
  }
  
  setQuoteData(){
    const test = this.selectedQuote();
    if (!test) {
      return;
    }

    const eqbdPremium = Number(test?.eqbdPremium ?? 0);
    if (!isNaN(eqbdPremium) && eqbdPremium !== 0) {
      this.closeWithSelectedQuote(test);
      return;
    }

    const hasRestrictedHighBpp = this.hasRestrictedOccupancyHighBpp(test);
    if (hasRestrictedHighBpp) {
      this.closeWithSelectedQuote(test);
      return;
    }

    const context = this.getEQBDReferralContext();
    const selectedVersionNo = Number(test?.versionNo ?? sessionStorage.getItem("versionNo"));
    if (!context || !selectedVersionNo) {
      this.openEqbdConfirmationDialog(test);
      return;
    }

    this.indicationService.GetEQBDReferralDetails(context.id, context.type).subscribe({
      next: (response: any) => {
        const records = Array.isArray(response?.data)
          ? response.data
          : (Array.isArray(response) ? response : []);
        const matchedVersion = records.find((item: any) => Number(item?.versionNo) === selectedVersionNo);
        const availability = matchedVersion?.isEQBDAvailabe;
        const skipPopup = availability !== null
          && availability !== undefined
          && availability !== ''
          && Number(availability) === 0;

        if (skipPopup) {
          this.closeWithSelectedQuote(test);
        } else {
          this.openEqbdConfirmationDialog(test);
        }
      },
      error: () => {
        this.openEqbdConfirmationDialog(test);
      }
    });
  }

  updateRagIndicationColor(feedBackType: string = "") {
    switch(feedBackType){
      case "Green":
        return "#238823"
      case "Amber":
        return "#FFBF00"
      case "Red":
        return "#FF0000"
      default:
        return "#000"
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

}
